/**
 * Unmatched demand (`/unmatched-demand`, `portfolio.view_demand`): the needs of Advisor runs and research that found no
 * strong match, newest first, without client names, filtered by status, country and industry. For portfolio and offer
 * owners, who archive an entry once handled (with the reason) so the Open view holds only what still needs a look.
 */

import './advisor.css';

import { useState } from 'react';
import { Search, Target } from 'lucide-react';

import { useCountries } from '@/api/queries';
import type { DemandStatus, UnmatchedDemandItem, UnmatchedDemandList, UnmatchedDemandParams } from '@/api/types';
import { FilterSelect } from '@/ui/FilterSelect';
import { PageHead } from '@/ui/PageHead';
import { Pagination } from '@/ui/Pagination';
import { LoadFailure, LoadingCard } from '@/ui/StateCard';

import { ArchiveDemandDialog } from './ArchiveDemandDialog';
import { DemandRow } from './DemandRow';
import { DEMAND_EMPTY, DEMAND_STATUS_LABELS } from './demandUi';
import { useDemandList } from './useDemandList';

/** The status filter's choices; "All statuses" (no choice) stands for `all`, so Open needs choosing explicitly. */
const STATUS_OPTIONS = (['open', 'archived'] as const).map((value) => ({ value, label: DEMAND_STATUS_LABELS[value] }));

function Empty({ status }: { status: DemandStatus }) {
  return (
    <div className="empty">
      <div className="e-icon"><Target aria-hidden="true" /></div>
      <h3>{DEMAND_EMPTY[status].title}</h3>
      <p>{DEMAND_EMPTY[status].text}</p>
    </div>
  );
}

function DemandTable({ data, params, update }: {
  data: UnmatchedDemandList;
  params: UnmatchedDemandParams;
  update: (patch: Partial<UnmatchedDemandParams>) => void;
}) {
  const [archiving, setArchiving] = useState<UnmatchedDemandItem | null>(null);
  return (
    <>
      <div className="table-scroll">
        <table className="data zebra compact" aria-label="Unmatched demand">
          <thead>
            <tr>
              <th scope="col">Needs</th>
              <th scope="col">Constraints</th>
              <th scope="col">Industry</th>
              <th scope="col">Country</th>
              <th scope="col">Source</th>
              <th scope="col">Recorded</th>
              <th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>{data.items.map((item) => <DemandRow key={item.id} item={item} onArchive={setArchiving} />)}</tbody>
        </table>
      </div>
      <Pagination
        page={params.page}
        pageSize={params.page_size}
        total={data.total}
        noun={data.total === 1 ? 'need' : 'needs'}
        onPage={(page) => update({ page })}
        onPageSize={(page_size) => update({ page_size })}
      />
      {archiving && <ArchiveDemandDialog item={archiving} onClose={() => setArchiving(null)} />}
    </>
  );
}

export function UnmatchedDemandPage() {
  const { params, update, text, setText, list } = useDemandList();
  const countries = useCountries();
  let body;
  if (list.isPending) body = <LoadingCard label="Loading unmatched demand" />;
  else if (list.error) body = <LoadFailure error={list.error} onRetry={() => void list.refetch()} />;
  else if (list.data.items.length === 0) body = <Empty status={params.status} />;
  else body = <DemandTable data={list.data} params={params} update={update} />;
  return (
    <>
      <PageHead title="Unmatched demand" subtitle="What clients asked for that no asset matched strongly" />
      <div className="card list-card">
        <div className="toolbar filter-panel">
          <div className="search-field">
            <Search aria-hidden="true" />
            <input type="search" value={text} placeholder="Filter by industry" aria-label="Filter by industry"
              onChange={(e) => setText(e.target.value)} />
          </div>
          <FilterSelect
            label="Filter by country"
            allLabel="All countries"
            value={params.country}
            options={(countries.data ?? []).filter((c) => c.code !== 'GLOBAL').map((c) => ({ value: c.code, label: c.name }))}
            onChange={(country) => update({ country })}
          />
          <FilterSelect
            label="Filter by status"
            allLabel="All statuses"
            value={params.status === 'all' ? null : params.status}
            options={STATUS_OPTIONS}
            onChange={(status) => update({ status: (status as DemandStatus | null) ?? 'all' })}
          />
        </div>
        <div aria-busy={list.isFetching}>{body}</div>
      </div>
    </>
  );
}
